"use client";

import Footer from "@/components/layout/footer";
import GroqLink from "@/components/ui/groq-link";
import { useScreen } from "@/hooks/use-screen";
import {
  DESKTOP_BREAKPOINT,
  INTERCOM_ACTION_COLOR,
  INTERCOM_BACKGROUD_COLOR,
} from "@/lib/consts";
import type { GeneralError } from "@/lib/errors/GeneralError";
import { useUser } from "@/shared/providers/user-provider";

import { logError } from "@/shared/logging/logger";
import { useTheme } from "next-themes";
import Image from "next/image";
import { useEffect } from "react";
import { useIntercom } from "react-use-intercom";
import { useGlobal } from "./providers/use-global";

type ErrorProps = {
  error: GeneralError;
  reset?: () => void;
};

export default function ErrorPage({ error, reset }: ErrorProps) {
  const { resolvedTheme } = useTheme();
  const { boot } = useIntercom();
  const { profile, currentOrg } = useUser();
  const { width: screenWidth } = useScreen();
  const { isIframe } = useGlobal();

  useEffect(() => {
    if (isIframe === false) {
      const customAttributes = {
        orgs: profile?.user.orgs?.data
          .map((org) => ({
            id: org.id,
            name: org.name,
            billingPlan: org.billing_plan,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, "en"))
          .map((org) => `"${org.id}:${org.name}:${org.billingPlan ?? "free"}"`)
          .join(",\n\n"),
        currentOrg: `${currentOrg}`,
      };

      boot({
        userId: profile?.user.id,
        email: profile?.user.email,
        userHash: profile?.user.intercom_hash,
        customAttributes,
        hideDefaultLauncher: screenWidth < DESKTOP_BREAKPOINT,
        actionColor: INTERCOM_ACTION_COLOR,
        backgroundColor: INTERCOM_BACKGROUD_COLOR,
      });
    }
  }, [profile, screenWidth, isIframe]);

  useEffect(() => {
    logError(error);
  }, [error]);

  return (
    <>
      <main className="overflow-hidden grid grid-cols-6 justify-center grow">
        <div className="flex justify-center col-span-4 col-start-2">
          <div className="grow max-w w-full flex flex-col px-5">
            <div className="flex flex-col h-full justify-center">
              <div className="grow flex flex-col items-center justify-center space-y-6 mt-[5vh]">
                <Image
                  className="sm:w-4/5 w-full sm:px-12 max-w-[800px]"
                  priority
                  src={
                    resolvedTheme === "dark"
                      ? "/updating-dark.gif"
                      : "/updating.gif"
                  }
                  alt="Updating"
                  width={400}
                  height={50}
                />
                <p className="text-2xl md:text-3xl text-gray-800 dark:text-white text-center">
                  {error?.code && <span>{error?.code} </span>}
                  {error?.message}
                </p>
                <div className="div flex min-w-min space-x-2 items-center mt-2">
                  <div className="grow" />
                  <GroqLink href={"/"}>GROQ.COM</GroqLink>

                  <span className="text-light-gray text-sm">|</span>

                  <GroqLink
                    href={{ pathname: process.env.NEXT_PUBLIC_CONTACT_US }}
                    isExternal
                  >
                    CONTACT US
                  </GroqLink>
                  <div className="grow" />
                </div>
                {error.showTryAgain && reset && (
                  <button
                    type="button"
                    className="bg-orange text-white font-semibold p-2 mt-5"
                    onClick={() => {
                      reset();
                    }}
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
